<script setup lang="ts">
import { computed } from 'vue'

export type IconName =
  | 'briefcase'
  | 'car'
  | 'home'
  | 'chart-bar'
  | 'sun'
  | 'banknotes'
  | 'calculator'
  | 'arrow-right'
  | 'moon'
  | 'menu'
  | 'check'
  | 'info'
  | 'exclamation'
  | 'x-mark'
  | 'share'
  | 'mail'
  | 'reset'
  | 'sparkles'

const props = withDefaults(
  defineProps<{
    name: IconName
    size?: number | string
  }>(),
  { size: 20 }
)

const paths: Record<IconName, string> = {
  briefcase:
    'M10 2h4a2 2 0 0 1 2 2v2H8V4a2 2 0 0 1 2-2ZM4 8V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2H4Zm0 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8H4Z',
  car: 'M4 13.25V16a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-1h6v1a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-2.758l-1.692-4.23A3 3 0 0 0 17.145 6H6.855a3 3 0 0 0-2.793 2.02L2.37 12.25a1 1 0 0 0 .93 1.35h.7ZM6.5 12a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z',
  home: 'M10.75 3.25a2.5 2.5 0 0 1 2.5 0l5.5 4a2.5 2.5 0 0 1 1 2v7.5a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 3.75 16.5V9.25a2.5 2.5 0 0 1 1-2l5.5-4ZM12 5l-5.5 4v7.5h3.5V14a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2v2.5h3.5V9.25L12 5Z',
  'chart-bar':
    'M3 16.5V19a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-2.5a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1ZM9 9v10a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-1a1 1 0 0 0-1 1ZM15 13v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1h-1a1 1 0 0 0-1 1ZM21 5v14a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-1a1 1 0 0 0-1 1Z',
  sun: 'M12 2.25a.75.75 0 0 1 .75.75v2.25a.75.75 0 0 1-1.5 0V3a.75.75 0 0 1 .75-.75ZM7.5 12a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0Zm8.97-6.97a.75.75 0 0 1 1.06 0l1.59 1.59a.75.75 0 1 1-1.06 1.06l-1.59-1.59a.75.75 0 0 1 0-1.06Zm4.28 5.22a.75.75 0 0 1 0 1.06l-1.59 1.59a.75.75 0 1 1-1.06-1.06l1.59-1.59a.75.75 0 0 1 1.06 0ZM12 17.25a.75.75 0 0 1 .75.75V20a.75.75 0 0 1-1.5 0v-1.95a.75.75 0 0 1 .75-.75Zm-7.72-4.28a.75.75 0 0 1 0 1.06l-1.59 1.59a.75.75 0 0 1-1.06-1.06l1.59-1.59a.75.75 0 0 1 1.06 0Zm1.06-7.72a.75.75 0 0 1 1.06 0l1.59 1.59a.75.75 0 1 1-1.06 1.06L5.34 2.57a.75.75 0 0 1 0-1.06ZM17.25 12a.75.75 0 0 1 .75-.75H20a.75.75 0 0 1 0 1.5h-1.95a.75.75 0 0 1-.75-.75Zm-14.5 0a.75.75 0 0 1 .75-.75H5a.75.75 0 0 1 0 1.5H3.5a.75.75 0 0 1-.75-.75Z',
  banknotes:
    'M12 4.5c-4.142 0-7.5 1.5-7.5 3.375V18A2.25 2.25 0 0 0 6.75 20.25h10.5A2.25 2.25 0 0 0 19.5 18V7.875c0-1.875-3.358-3.375-7.5-3.375Zm0 1.5c3.575 0 6 1.13 6 1.875S15.575 9.75 12 9.75 6 8.62 6 7.875 8.425 6 12 6ZM6.75 15a2.25 2.25 0 0 1 2.25-2.25h6a2.25 2.25 0 0 1 0 4.5H9A2.25 2.25 0 0 1 6.75 15Z',
  calculator:
    'M5 4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4Zm2 0v16h10V4H7Zm2 2h2v2H9V6Zm0 4h2v2H9v-2Zm0 4h2v2H9v-2Zm4-8h2v2h-2V6Zm0 4h2v2h-2v-2Zm0 4h2v2h-2v-2Z',
  'arrow-right': 'M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3',
  moon: 'M21.752 15.002A9.718 9.718 0 0 1 12.002 21c-5.385 0-9.75-4.365-9.75-9.75 0-4.67 3.285-8.586 7.688-9.56a.75.75 0 0 1 .812 1.146 8.25 8.25 0 0 0 1.363 15.164.75.75 0 0 1 .637.002Z',
  menu: 'M3 6.75A.75.75 0 0 1 3.75 6h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.75Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z',
  check:
    'M18.28 7.72a.75.75 0 0 0-1.06-1.06l-6.5 6.5a.75.75 0 0 1-1.06 0l-2.75-2.75a.75.75 0 0 0-1.06 1.06l3.805 3.805a1.25 1.25 0 0 0 1.77 0l7.345-7.345Z',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm.75-9v4a.75.75 0 0 1-1.5 0v-4a.75.75 0 0 1 1.5 0Zm0-3.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z',
  exclamation:
    'M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-.75 6.75a.75.75 0 0 1 1.5 0v4.5a.75.75 0 0 1-1.5 0V9Zm.75 9a1.125 1.125 0 1 0 0-2.25 1.125 1.125 0 0 0 0 2.25Z',
  'x-mark':
    'M6.28 5.22a.75.75 0 0 0-1.06 1.06L10.94 12l-5.72 5.72a.75.75 0 1 0 1.06 1.06L12 13.06l5.72 5.72a.75.75 0 1 0 1.06-1.06L13.06 12l5.72-5.72a.75.75 0 0 0-1.06-1.06L12 10.94 6.28 5.22Z',
  share:
    'M15 8a3 3 0 1 0-2.977-2.63l-4.94 2.47a3 3 0 1 0 0 4.32l4.94 2.47A3.001 3.001 0 1 0 15 13.999v-.001l-4.94-2.47a3.001 3.001 0 0 0 0-1.058L15 8Z',
  mail: 'M2.25 6.75A2.25 2.25 0 0 1 4.5 4.5h15a2.25 2.25 0 0 1 2.25 2.25v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75Zm2.7-.75 7.05 4.7 7.05-4.7H4.95ZM20.25 8.3l-7.42 4.95a1.5 1.5 0 0 1-1.66 0L3.75 8.3v8.95a.75.75 0 0 0 .75.75h15a.75.75 0 0 0 .75-.75V8.3Z',
  reset:
    'M9.195 18.44c1.25.713 2.71 1.12 4.28 1.12 4.97 0 9-4.03 9-9s-4.03-9-9-9-9 4.03-9 9h2c0-3.86 3.14-7 7-7s7 3.14 7 7-3.14 7-7 7c-1.14 0-2.22-.29-3.16-.79L12 13h9V5l-1.79 4.37A8.985 8.985 0 0 0 13.44 7.1l-.78-1.87A9.06 9.06 0 0 1 9.195 18.44Z',
  sparkles:
    'M15.362 5.214A8.252 8.252 0 0 1 12 0 8.252 8.252 0 0 1 8.638 5.214L5.757 8.095a8.252 8.252 0 0 0-5.214 3.362 8.252 8.252 0 0 0 5.214 3.362l2.881 2.881A8.252 8.252 0 0 0 12 24a8.252 8.252 0 0 0 3.362-5.214l2.881-2.881a8.252 8.252 0 0 0 5.214-3.362 8.252 8.252 0 0 0-5.214-3.362l-2.881-2.881Z',
}

const sizeValue = computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size))
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    :width="sizeValue"
    :height="sizeValue"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    class="icon"
  >
    <path :d="paths[name]" />
  </svg>
</template>

<style scoped>
.icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
}
</style>
